"use client";

import React, { useState } from "react";
import Link from "next/link";
import { useCart } from "../data/CartContext";
import { track } from "../lib/track";
import type { Bundle } from "../data/bundles";

export default function BundleCard({ bundle }: { bundle: Bundle }) {
  const { addToCart } = useCart();
  const [showNote, setShowNote] = useState(false);

  const numericPrice = Number(String(bundle.price).replace(/[^\d.-]/g, "")) || 0;

  const handleAddToCart = () => {
    addToCart({ id: bundle.id, title: bundle.title, price: bundle.price, qty: 1, totalPrice: numericPrice });
    track(
      "add_to_cart",
      { label: `Added 1 × ${bundle.title}`, component: "BundleCard" },
      { event: "add_to_cart", label: `Added 1 × ${bundle.title}`, component: "BundleCard", meta: { timestamp: Date.now() } }
    );
    setShowNote(true);
    setTimeout(() => setShowNote(false), 4000);
  };

  return (
    <article className="bg-[var(--mint-dk)] text-[var(--text-on-dark)] rounded-2xl overflow-hidden shadow-xl flex flex-col md:flex-row">
      {/* Left: identity */}
      <div className="md:w-2/5 bg-[var(--mint-dk)] p-6 flex flex-col justify-center gap-3">
        <span className="inline-block self-start text-xs font-semibold tracking-widest uppercase bg-amber-500 text-white px-3 py-1 rounded-full">
          {bundle.badge}
        </span>
        <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">{bundle.title}</h3>
        <p className="text-sm text-white/70 uppercase tracking-wider font-medium">{bundle.subtitle}</p>
        <p className="text-sm text-white/80 leading-relaxed mt-1">{bundle.tagline}</p>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-2xl font-extrabold">{bundle.price}</span>
          <button
            onClick={handleAddToCart}
            className="bg-amber-500 hover:bg-amber-400 text-white font-semibold py-2 px-5 rounded-md transition"
          >
            Add to Cart
          </button>
          <Link
            href={`/shop/bundles/${bundle.slug}`}
            className="text-sm text-white/70 underline underline-offset-4 hover:text-white"
          >
            See what's inside →
          </Link>
        </div>

        {showNote && (
          <div className="mt-2 text-sm bg-white/10 border border-white/20 rounded-lg py-2 px-3 text-white">
            Added <strong>{bundle.title}</strong> to cart.{" "}
            <Link href="/shop/cart" className="underline">View cart →</Link>
          </div>
        )}
      </div>

      {/* Right: what's included */}
      <div className="md:w-3/5 p-6 flex flex-col justify-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">
          What's included
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {bundle.items.map((item) => (
            <div key={item.name} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="font-semibold text-sm">{item.name}</p>
              <p className="text-xs text-white/50 mb-2">{item.size}</p>
              <ul className="space-y-1">
                {item.benefits.slice(0, 3).map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-white/75">
                    <span className="mt-px shrink-0">✅</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {bundle.topBenefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-white/70">
              <span className="shrink-0 mt-px">🌿</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}
